import { Box, Button, Grid } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import ModalContext, { ConfirmationServiceContext } from '../../context/modal';
import { ContainerInfo } from '../Containers/ContainerInfo';
import { InfoField } from '../Containers/InfoField';
import useContainerApiRoutes from '../../hooks/api/useContainerApiRoutes';
import useClientApiRoutes from '../../hooks/api/useClientApiRoutes';
import { Client, ContainerStatus } from '../../../types';
import { BlackButton } from '../Form';
import SnackBarContext from '../../context/snackbar';
import useUtils from '../../hooks/useUtils';

type Props = {};

const ViewContainerStackedModal = (props: Props) => {
  const { setDrawerOpen, container, setContainer } = useContext(ModalContext);
  const { getContainer, deleteContainer, emergencyCancellationContainer, cancelReuse } = useContainerApiRoutes();
  const { getClient } = useClientApiRoutes();
  const { showSnack } = useContext(SnackBarContext);
  const { showConfirmationModal, setOpenId } = useContext(ConfirmationServiceContext);
  const [client, setClient] = useState<Client | undefined>();
  const { getContainerTypeName, formatAvailableTime } = useUtils();

  const editableStatuses = useRef<ContainerStatus[]>(['available', 'assigned', 'internalUse']).current;
  const cancellableStatuses = useRef<ContainerStatus[]>(['assigned', 'internalUse']).current;
  // A container that went through the shipping-line re-use request flow
  // (pending, or accepted for re-use) is cancelled via the re-use route —
  // the line is told to cancel — not via the marketplace emergency path.
  const reuseCancellable =
    !!container?.requested_by_user_id && ['pending', 'assigned', 'internalUse'].includes(container.status);

  useEffect(() => {
    if (container?._id) {
      getContainer(container._id).then((returnedContainer) => {
        setContainer(returnedContainer);
      });
    }
  }, [container?._id, getContainer, setContainer]);

  useEffect(() => {
    if (container?.client_id) {
      getClient(container.client_id).then((client) => {
        setClient(client);
      });
    }
  }, [container?.client_id, getClient]);

  const editContainer = () => {
    setDrawerOpen('edit-container');
  };

  const handleRemoveFromStack = async () => {
    if (container?._id) {
      const res = await showConfirmationModal({
        title: `Remove Container ${getContainerTypeName(container)} #${container.containerCode}?`,
        contentText:
          'Do you wish to permanently delete this container from your Stack?\nPlease remember to arrange dehire of this container if applicable',
      });

      if (res && res.confirmed === true) {
        await deleteContainer(container._id);
        showSnack('Container Removed!', 'success');
        setDrawerOpen(undefined);
        setOpenId(undefined);
      }
    }
  };

  const handleCancellation = async () => {
    if (container?._id) {
      const res = await showConfirmationModal({
        title: 'Cancel Container?',
        contentText: 'Do you wish to cancel this container and move it into the Archived tab?',
      });
      if (res && res.confirmed === true) {
        await emergencyCancellationContainer(container);
        showSnack('Container Cancelled!', 'success');
        setDrawerOpen(undefined);
        setOpenId(undefined);
      }
    }
  };

  const handleCancelReuse = async () => {
    if (!container?._id) return;
    const res = await showConfirmationModal({
      title: `Cancel re-use of container #${container.containerCode}?`,
      contentText:
        container.status === 'pending'
          ? 'This withdraws the re-use request. The shipping line will be told to cancel it and the container moves to your Archived tab.'
          : 'This cancels the confirmed re-use (e.g. the container has been dehired). The shipping line will be told to cancel it, it will not be billed as a re-use, and the container moves to your Archived tab.',
      reasonLabel: 'Reason (sent to the shipping line)',
      confirmLabel: 'Cancel re-use',
    });
    if (res && res.confirmed === true && res.reason) {
      await cancelReuse(container._id, res.reason);
      showSnack('Re-use cancelled — the shipping line has been notified.', 'success');
      setDrawerOpen(undefined);
      setOpenId(undefined);
    }
  };

  return container ? (
    <Box>
      <ContainerInfo container={container} client={client} />
      <Grid container spacing={4} color="unset">
        <Grid item xs={4}>
          <InfoField heading="AVAILABLE FROM" detail={formatAvailableTime(container)} />
        </Grid>
        <Grid item xs={4}>
          {client ? <InfoField heading="LOCATION FROM" detail={client.address} /> : null}
        </Grid>
        <Grid item xs={4}>
          {container.goingTo && container.status === 'internalUse' ? (
            <InfoField heading="LOCATION TO" detail={container.goingTo?.address} />
          ) : null}
        </Grid>
      </Grid>
      <Grid container spacing={4} color="unset">
        <Grid item xs={12}>
          {client ? <InfoField heading="BASIC SITE INSTRUCTIONS" detail={client.additionalSiteInstructions} /> : null}
        </Grid>
      </Grid>
      <Grid container spacing={4} color="unset">
        <Grid item xs={12}>
          {client ? (
            <InfoField
              heading="MIMIMUM HEATH AND SAFETY REQUIREMENTS"
              detail={client.minimumHealthSafetyRequirements}
            />
          ) : null}
        </Grid>
      </Grid>
      <Grid container spacing={4} color="unset">
        <Grid item xs={12}>
          <Button
            size="large"
            disabled={!editableStatuses.includes(container.status)}
            variant="contained"
            color="primary"
            sx={{ mr: 2 }}
            onClick={editContainer}
          >
            Edit Container
          </Button>
          <Button
            size="large"
            disabled={container.status !== 'available'}
            variant="outlined"
            color="primary"
            sx={{ mr: 2 }}
            onClick={handleRemoveFromStack}
          >
            Remove From Stack
          </Button>
          {reuseCancellable ? (
            <BlackButton onClick={handleCancelReuse}>
              {container.status === 'pending' ? 'Cancel Re-use Request' : 'Cancel Re-use'}
            </BlackButton>
          ) : (
            <BlackButton disabled={!cancellableStatuses.includes(container.status)} onClick={handleCancellation}>
              Emergency Cancellation
            </BlackButton>
          )}
        </Grid>
      </Grid>
    </Box>
  ) : null;
};

export default ViewContainerStackedModal;
